/**
 * Card geometry knobs — the inner proportions still open per docs/DESIGN.md #15 (the card
 * silhouette itself is settled at 5:7 and out of scope). The drawing code (cards.js,
 * card-backs.js, card-motifs.js) reads `CARD_GEOMETRY` at draw time instead of the constants
 * it used to hardcode, so `/card-lab`'s Golden tab can retune them live and repaint every card
 * — including the Hand tab, since this state is global, not tab-scoped.
 *
 * The defaults are the shipped production values — changing one here changes production visuals
 * and is done only on a recorded verdict (DESIGN #15), with the affected snapshots regenerated
 * deliberately in the same commit. `CARD_GEOMETRY_DEFAULTS` is the frozen reference the lab
 * resets to and tests restore after mutating the live object.
 */
export const CARD_GEOMETRY_DEFAULTS = Object.freeze({
  tallyAspect: 1.618, // point-value tally: ry = rx * tallyAspect — verdict + why: CARDS.md § Golden-ratio verdicts — "Point-value tally arc aspect" row
  tallyRunBase: 10, // tally arc rx = tallyRunBase + tallyRunIncrement * k (k = 1..points) — no lab slider: a uniform offset, not a proportion, so it carries no golden-ratio verdict of its own (unlike tallyAspect/tallyRunIncrement) — nothing to judge live
  tallyRunIncrement: 12, // paired with tallyAspect — verdict + why: CARDS.md § Golden-ratio verdicts — "Point-value tally arc aspect" row
  edgeThickness: 5, // the L-border's two rects (spine width / shelf height)
  mandalaHRadius: 61.8, // card-back mandala's horizontal pip radius: vRadius/φ on the fixed vRadius 100 — verdict + why: CARDS.md § Golden-ratio verdicts — "Card-back mandala" row
  // Per-rank uniform scale on the face motif glyph, about the card center (125, 175). Glyph
  // only: the atmosphere glow stays outside the scale wrapper (see card-motifs.js#getAtmosphere)
  // and is governed by the atmosphere knobs (atmosphereScaleAce / faceAtmosphereRadius) alone.
  motifScaleJack: 1.09, // verdict + why: CARDS.md § Golden-ratio verdicts — "Jack motif" row
  motifScaleQueen: 1.3, // verdict + why: CARDS.md § Golden-ratio verdicts — "Queen motif" row
  motifScaleKing: 1,
  // Per-suit ace motif scale — the four aces' glyphs (sphere/heart/leaf/acorn) differ enough in
  // geometry that a single shared value can't be judged or tuned per suit.
  motifScaleAceDiamonds: 1.236, // verdict + why: CARDS.md § Golden-ratio verdicts — "Diamonds ace motif" row
  motifScaleAceHearts: 1.146, // verdict + why: CARDS.md § Golden-ratio verdicts — "Hearts ace motif" row
  motifScaleAceSpades: 1.06, // verdict + why: CARDS.md § Golden-ratio verdicts — "Spades ace motif" row
  motifScaleAceClubs: 1.236, // verdict + why: CARDS.md § Golden-ratio verdicts — "Clubs ace motif" row
  // Atmosphere sizing is split in two: the ace side stays a scale (two concentric rings, so a
  // multiplier keeps them proportional to each other); the face side is a direct radius (one
  // ring — nothing for a scale to stay proportional TO). Faces deliberately keep the SINGLE
  // ring: the two-ring stepped glow stays ace-only.
  atmosphereScaleAce: 1, // uniform scale on the two ace-ring radii, about the card center — the glow's ONLY size knob for aces (motif scale no longer reaches it)
  faceAtmosphereRadius: 91, // the single face-card atmosphere ring's radius — verdict + why: CARDS.md § Golden-ratio verdicts — "Face atmosphere ring" row
  cornerScale: 0.9, // uniform scale on each corner-label ensemble (rank text + corner pip), anchored at its visual corner so it grows in place (cards.js#CORNER_SCALE_ANCHOR) — verdict + why: CARDS.md § Golden-ratio verdicts — "Corner ensemble (rank char + pip)" row
  cornerPipScale: 1.2, // scale on the corner SUIT PIP alone, about its own center — the rank char stays put (multiplies the pip's baked 1.8; composes with cornerScale) — verdict + why: CARDS.md § Golden-ratio verdicts — "Corner ensemble (rank char + pip)" row
  // ViewBox units each corner ensemble slides toward its own corner, per axis (positive = less
  // edge padding). Split X/Y because the corner content is centered on a fixed column (x=38):
  // the wide "10" sits ~7 units off the left edge where the "A" has ~18, but has MORE top room
  // (smaller font, cap top ~22 vs ~16) — one diagonal dial can't serve both.
  cornerNudgeX: 2, // verdict + why: CARDS.md § Golden-ratio verdicts — "Corner ensemble (rank char + pip)" row
  cornerNudgeY: 5, // verdict + why: CARDS.md § Golden-ratio verdicts — "Corner ensemble (rank char + pip)" row
  // Per-suit multiplier on the corner pip's scale, equalizing perceived pip size (√ of ink-bbox
  // area) across suits — solved live by card-lab's "Corners → ref" button
  // (dev/card-lab-corner-solver.js). The anchor is the MEDIAN of the 4 suits' √bbox-area (not
  // the mean) — diamonds' sphere+stem sits well below the other three's tightly-clustered
  // organic silhouettes, and a mean would let that one outlier drag the whole target toward it.
  // Verdict + why: CARDS.md § Golden-ratio verdicts — "Corner ensemble (rank char + pip)" row.
  cornerPipSuitFactor: {
    diamonds: 1.116,
    hearts: 0.979,
    spades: 0.985,
    clubs: 1.015,
  },
  // Per-card ink-normalized corner correction, keyed `"<suit>:<label>"` (label = the rendered
  // rank text, so locale AND rankStyle are handled for free — German U/O, English J/Q, and French
  // B/D each share one K/A entry). Value `{ gx, gy, pdx, pipDy }`: gx/gy shift the whole corner
  // ensemble (final units, same frame as cornerNudgeX/Y); pdx/pipDy shift the pip alone, local
  // units inside the corner group. Missing key -> zeros (today's authored position). All 48
  // solved live by the same button (dev/card-lab-corner-solver.js) — the B/D entries (Bube/Dame,
  // card-preferences design § B) landed 2026-08-03, solved against the same live geometry as the
  // other 40.
  cornerInkOffsets: {
    "diamonds:7": { gx: -3.3, gy: 0, pdx: 5.1, pipDy: 9.9 },
    "diamonds:8": { gx: -2.2, gy: 0.7, pdx: 3.9, pipDy: 10.9 },
    "diamonds:9": { gx: -2.6, gy: 0.7, pdx: 4.4, pipDy: 10.3 },
    "diamonds:10": { gx: 7.7, gy: -4.5, pdx: -7.1, pipDy: 9.7 },
    "diamonds:J": { gx: -3.6, gy: 0, pdx: 5.4, pipDy: 10.8 },
    "diamonds:Q": { gx: 2.1, gy: 0.7, pdx: -0.9, pipDy: 10.9 },
    "diamonds:K": { gx: -3.6, gy: 0, pdx: 5.4, pipDy: 9.8 },
    "diamonds:A": { gx: 0.7, gy: 0, pdx: 0.6, pipDy: 5.6 },
    "diamonds:U": { gx: -1.8, gy: 0, pdx: 3.4, pipDy: 10.9 },
    "diamonds:O": { gx: 2.1, gy: 0.7, pdx: -0.9, pipDy: 10.9 },
    "diamonds:B": { gx: -4.2, gy: 0, pdx: 6.1, pipDy: 10.2 },
    "diamonds:D": { gx: -1.8, gy: 0, pdx: 3.4, pipDy: 10.2 },
    "hearts:7": { gx: -3.3, gy: 0, pdx: 6.4, pipDy: 1.9 },
    "hearts:8": { gx: -2.2, gy: 0.7, pdx: 5.2, pipDy: 2.7 },
    "hearts:9": { gx: -2.6, gy: 0.7, pdx: 5.7, pipDy: 2.2 },
    "hearts:10": { gx: 7.7, gy: -4.5, pdx: -5.8, pipDy: 2.5 },
    "hearts:J": { gx: -3.6, gy: 0, pdx: 6.7, pipDy: 2.7 },
    "hearts:Q": { gx: 2.1, gy: 0.7, pdx: 0.4, pipDy: 4.5 },
    "hearts:K": { gx: -3.6, gy: 0, pdx: 6.7, pipDy: 1.9 },
    "hearts:A": { gx: 0.7, gy: 0, pdx: 1.9, pipDy: 1.9 },
    "hearts:U": { gx: -1.8, gy: 0, pdx: 4.7, pipDy: 2.7 },
    "hearts:O": { gx: 2.1, gy: 0.7, pdx: 0.4, pipDy: 2.6 },
    "hearts:B": { gx: -4.2, gy: 0, pdx: 7.4, pipDy: 1.9 },
    "hearts:D": { gx: -1.8, gy: 0, pdx: 4.7, pipDy: 1.9 },
    "spades:7": { gx: -3.3, gy: 0, pdx: 4.4, pipDy: 5.4 },
    "spades:8": { gx: -2.2, gy: 0.7, pdx: 3.2, pipDy: 7.1 },
    "spades:9": { gx: -2.6, gy: 0.7, pdx: 3.7, pipDy: 6 },
    "spades:10": { gx: 7.7, gy: -4.5, pdx: -7.8, pipDy: 5.1 },
    "spades:J": { gx: -3.6, gy: 0, pdx: 4.7, pipDy: 6.6 },
    "spades:Q": { gx: 2.1, gy: 0.7, pdx: -1.6, pipDy: 7.1 },
    "spades:K": { gx: -3.6, gy: 0, pdx: 4.7, pipDy: 4.6 },
    "spades:A": { gx: 0.7, gy: 0, pdx: -0.1, pipDy: 0.5 },
    "spades:U": { gx: -1.8, gy: 0, pdx: 2.7, pipDy: 7.1 },
    "spades:O": { gx: 2.1, gy: 0.7, pdx: -1.6, pipDy: 7.1 },
    "spades:B": { gx: -4.2, gy: 0, pdx: 5.4, pipDy: 6.3 },
    "spades:D": { gx: -1.8, gy: 0, pdx: 2.7, pipDy: 6.3 },
    "clubs:7": { gx: -3.3, gy: 0, pdx: 3, pipDy: 4.9 },
    "clubs:8": { gx: -2.2, gy: 0.7, pdx: 1.7, pipDy: 5.8 },
    "clubs:9": { gx: -2.6, gy: 0.7, pdx: 2.2, pipDy: 5.2 },
    "clubs:10": { gx: 7.7, gy: -4.5, pdx: -9.3, pipDy: 4.8 },
    "clubs:J": { gx: -3.6, gy: 0, pdx: 3.2, pipDy: 5.7 },
    "clubs:Q": { gx: 2.1, gy: 0.7, pdx: -3, pipDy: 5.8 },
    "clubs:K": { gx: -3.6, gy: 0, pdx: 3.2, pipDy: 3.1 },
    "clubs:A": { gx: 0.7, gy: 0, pdx: -1.5, pipDy: -0.8 },
    "clubs:U": { gx: -1.8, gy: 0, pdx: 1.2, pipDy: 5.8 },
    "clubs:O": { gx: 2.1, gy: 0.7, pdx: -3, pipDy: 5.8 },
    "clubs:B": { gx: -4.2, gy: 0, pdx: 4, pipDy: 5 },
    "clubs:D": { gx: -1.8, gy: 0, pdx: 1.2, pipDy: 5 },
  },
  pipSizeScale: 1.618, // uniform scale of each number-card pip's OWN size only — positions stay put — verdict + why: CARDS.md § Golden-ratio verdicts — "Number-card pip size" row
});

export const CARD_GEOMETRY = { ...CARD_GEOMETRY_DEFAULTS };
